import { useEffect, useState } from 'react';
import { getApiLanguage, VISIME_LANGUAGE_CHANGED } from '../config/apiLanguage';
import {
  applyPageSeoToDocument,
  buildWebPageJsonLd,
  captureSeoSnapshot,
  restoreSeoSnapshot,
  type ApplyPageSeoInput,
} from './pageSeo';

export type PageSeoOptions = Omit<ApplyPageSeoInput, 'htmlLang'> & {
  includeWebPageSchema?: boolean;
};

export function usePageSeo(options: PageSeoOptions) {
  const {
    title,
    description,
    path,
    imageUrl,
    robots,
    hreflang = false,
    jsonLd,
    jsonLdSlot,
    includeWebPageSchema = false,
  } = options;

  const [langTick, setLangTick] = useState(0);
  useEffect(() => {
    const onLang = () => setLangTick((n) => n + 1);
    window.addEventListener(VISIME_LANGUAGE_CHANGED, onLang);
    return () => window.removeEventListener(VISIME_LANGUAGE_CHANGED, onLang);
  }, []);

  useEffect(() => {
    const snapshot = captureSeoSnapshot();
    const lang = getApiLanguage() === 'de' ? 'de' : 'en';
    const blocks = jsonLd
      ? Array.isArray(jsonLd)
        ? [...jsonLd]
        : [jsonLd]
      : [];
    if (includeWebPageSchema && description) {
      blocks.push(buildWebPageJsonLd(title, description, path));
    }
    const { jsonLdIds } = applyPageSeoToDocument({
      title,
      description,
      path,
      imageUrl,
      robots,
      hreflang,
      htmlLang: lang,
      jsonLd: blocks.length ? blocks : undefined,
      jsonLdSlot,
    });
    return () => {
      restoreSeoSnapshot(snapshot, jsonLdIds);
    };
  }, [title, description, path, imageUrl, robots, hreflang, jsonLd, jsonLdSlot, includeWebPageSchema, langTick]);
}

export function useMarketingPageMeta(title: string, description?: string, path?: string) {
  usePageSeo({
    title,
    description,
    path: path ?? (typeof window !== 'undefined' ? window.location.pathname : '/landing'),
    hreflang: true,
    includeWebPageSchema: Boolean(description),
    robots: 'index, follow',
  });
}

export function useNoIndexPageMeta(title: string, path?: string) {
  usePageSeo({
    title,
    path: path ?? (typeof window !== 'undefined' ? window.location.pathname : '/'),
    robots: 'noindex, nofollow',
    hreflang: false,
  });
}
